import { getUserType } from "./helperMethods";

/** Prototype-aligned org name styling in grids and banners. */
export const GRID_ORG_NAME_LINK_CLASS =
  "cursor-pointer text-gray-900 hover:text-indigo-600 transition-colors duration-200 break-words";

export function normalizeOrgId(value) {
  if (value == null || value === "") return null;
  return String(value);
}

export function idsMatch(a, b) {
  const left = normalizeOrgId(a);
  const right = normalizeOrgId(b);
  if (!left || !right) return false;
  return left === right;
}

export function isProviderVisibleInDiscovery(item) {
  if (typeof item?.provider_visible_in_discovery === "boolean") {
    return item.provider_visible_in_discovery;
  }
  if (typeof item?.provider_discovery_status === "boolean") {
    return item.provider_discovery_status;
  }
  if (typeof item?.discovery_status === "boolean") {
    return item.discovery_status;
  }
  return Boolean(normalizeOrgId(item?.provider_id ?? item?.providerId));
}

export function resolveAdminManageSponsorOrgPath(sponsorId) {
  const id = normalizeOrgId(sponsorId);
  return id ? `/Backend/Users/AddSponsors/${id}` : null;
}

export function resolveAdminManageVendorOrgPath(providerId) {
  const id = normalizeOrgId(providerId);
  return id ? `/Backend/Users/AddVendorAccount/${id}` : null;
}

export function resolvePublicVendorProfilePath(providerId) {
  const id = normalizeOrgId(providerId);
  return id ? `/provider/${id}/overview` : null;
}

export function resolveSponsorSelfManageOrgPath() {
  return "/Sponsor/Team/Manage";
}

export function resolveVendorSelfManageOrgPath() {
  return "/user/team/profile/edit";
}

/**
 * Resolve a navigation target for a sponsor organization name in a grid row.
 * @returns {string|null}
 */
export function resolveGridSponsorOrgHref({
  viewerRole,
  viewerSponsorId,
  rowSponsorId,
}) {
  const viewerType = getUserType(viewerRole);
  if (viewerType === "vendor") return null;

  const targetId = normalizeOrgId(rowSponsorId);
  if (!targetId) return null;

  if (viewerType === "admin") {
    return resolveAdminManageSponsorOrgPath(targetId);
  }

  if (viewerType === "sponsor") {
    if (idsMatch(viewerSponsorId, targetId)) {
      return resolveSponsorSelfManageOrgPath();
    }
    return null;
  }

  return null;
}

/**
 * Resolve a navigation target for a vendor organization name in a grid row.
 * @returns {string|null}
 */
export function resolveGridVendorOrgHref({
  viewerRole,
  viewerProviderId,
  rowProviderId,
  rowItem,
}) {
  const viewerType = getUserType(viewerRole);
  const targetId = normalizeOrgId(rowProviderId ?? rowItem?.provider_id ?? rowItem?.providerId);
  if (!targetId) return null;

  if (viewerType === "admin") {
    return resolveAdminManageVendorOrgPath(targetId);
  }

  if (viewerType === "vendor") {
    if (idsMatch(viewerProviderId, targetId)) {
      return resolveVendorSelfManageOrgPath();
    }
    return null;
  }

  if (viewerType === "sponsor") {
    if (!isProviderVisibleInDiscovery(rowItem)) return null;
    return resolvePublicVendorProfilePath(targetId);
  }

  return null;
}

export function canNavigateRfiRowToDetails(item) {
  return Boolean(!item?.v1 && item?.questionnaire_id);
}

export function resolveRfiRowDetailsPath(item) {
  if (!canNavigateRfiRowToDetails(item)) return null;
  return `/RFI/${item.questionnaire_id}/Details`;
}

export function canNavigateAssessmentRowToDetails(item) {
  const id = item?.vqa_request_id ?? item?.vqaRequestId;
  return id != null && String(id).trim() !== "";
}

export function resolveAssessmentRowDetailsPath(item, actionVariant) {
  if (!canNavigateAssessmentRowToDetails(item)) return null;
  const id = item?.vqa_request_id ?? item?.vqaRequestId;
  if (actionVariant === "admin") {
    return null;
  }
  return `/Assessment/${id}/Details`;
}

export function buildAdminVendorUserManagePath(row) {
  const params = new URLSearchParams(
    Object.fromEntries(
      Object.entries({
        userId: row?.provider_user_id,
        providerName: row?.provider_name,
        clerkId: row?.provider_user_clerk_id,
        providerId: row?.provider_id,
      }).map(([k, v]) => [k, v ?? ""])
    )
  );
  return `/Backend/Users/AddProviderUser?${params.toString()}`;
}

export function buildAdminSponsorUserManagePath(row) {
  const userId = row?.id ?? row?.user_id;
  return userId ? `/Backend/Users/Sponsors/AddUser?userId=${userId}` : null;
}

export function resolveAdminManageBackendUserPath(userId) {
  const id = normalizeOrgId(userId);
  return id ? `/Backend/Users/BackendUsers/${id}/manage` : null;
}

export function mapCalendarRowToOrgRow(row) {
  if (!row) return {};
  return {
    provider_id: row.provider_id ?? row.providerId ?? row.id,
    sponsor_id: row.sponsor_id ?? row.sponsorId,
    provider_visible_in_discovery:
      row.provider_visible_in_discovery ??
      row.provider_discovery_status ??
      row.discovery_status,
  };
}

export function mapLibraryItemToOrgRow(item) {
  if (!item) return {};
  return {
    provider_id: item.provider_id ?? item.providerId,
    sponsor_id: item.sponsor_id ?? item.sponsorId,
    provider_visible_in_discovery:
      item.provider_visible_in_discovery ??
      item.provider_discovery_status ??
      item.discovery_status,
    provider_name: item.provider_name,
  };
}

export function mapRfiDetailsToOrgRow(details) {
  if (!details) return {};
  return {
    provider_id: details.provider_id ?? details.providerId,
    sponsor_id: details.sponsor_id ?? details.sponsorId,
    provider_visible_in_discovery:
      details.provider_visible_in_discovery ??
      details.provider_discovery_status ??
      details.discovery_status,
  };
}

export function mapAssessmentDetailsToOrgRow(details) {
  if (!details) return {};
  const provider =
    details.provider ??
    details.vqa?.provider ??
    details.vqa?.Provider ??
    null;
  const sponsor =
    details.sponsor ??
    details.vqa?.sponsor ??
    null;
  const primarySponsor = (details.vqa_request_sponsors ?? []).find(
    (s) => s?.is_primary_sponsor === true
  );
  return {
    provider_id:
      provider?.provider_id ??
      provider?.providerId ??
      details.provider_id,
    sponsor_id:
      sponsor?.sponsor_id ??
      sponsor?.sponsorId ??
      primarySponsor?.sponsor_id ??
      primarySponsor?.sponsorId ??
      details.sponsor_id,
    provider_visible_in_discovery:
      provider?.discovery_status ??
      details.provider_discovery_status ??
      details.discovery_status,
    provider_name:
      provider?.provider_name ??
      details.provider_name ??
      details.vendorName,
    sponsor_name:
      sponsor?.sponsor_name ??
      primarySponsor?.sponsor_name ??
      details.sponsor_name ??
      details.sponsorName,
  };
}

/** My Vendors vendor -> org row carrying its real Discovery visibility (never assumed visible). */
export function myVendorOrgRow(vendor) {
  return {
    provider_id: vendor?.id,
    provider_visible_in_discovery: vendor?.provider?.discovery_status,
  };
}

/** Whether a My Vendors vendor may open its public profile (hidden vendors may not). */
export function isMyVendorProfileVisible(vendor) {
  return Boolean(vendor?.id) && isProviderVisibleInDiscovery(myVendorOrgRow(vendor));
}

/** Row item for org/vendor column in library, VQQ, and assessment grids. */
export function toVendorOrgRow(item) {
  return item?.provider_id != null || item?.providerId != null
    ? item
    : mapLibraryItemToOrgRow(item);
}
